/**
 * Polo "rising age" convention.
 *
 * In the polo world, every horse's age turnover happens on 1 January of
 * each calendar year regardless of the actual birthday. From 1 Jan
 * onward, a horse aged N is referred to as "Rising N+1" — meaning it
 * will turn N+1 during the year. This label sits alongside the stored
 * numeric age until the admin manually bumps the age in the DB (which
 * they do at some point during the year, typically when the horse
 * physically has its birthday).
 *
 * Business rules confirmed with the owner:
 *   - The rising label + ↑ arrow show ALL YEAR (1 Jan → 31 Dec).
 *   - On the next 1 Jan the label flips to the next number (i.e. if
 *     the DB age hasn't been updated, "Rising 5" becomes "Rising 6" on
 *     Jan 1 the following year). This is why the label is derived
 *     purely from the stored age — no scheduled DB migration required.
 *   - The convention is dormant until 1 January 2027 UTC; before that
 *     date the display is unchanged.
 *
 * All helpers here are pure — they take an explicit `now` for testing.
 */

// The convention doesn't activate until this instant. Before it, every
// call to `poloAge()` returns `{ isRising: false }` so the app looks
// exactly as it did pre-feature.
export const RISING_EPOCH_ISO = "2027-01-01T00:00:00Z";
export const RISING_EPOCH_DATE = new Date(RISING_EPOCH_ISO);

/**
 * Parse the raw `age` field (which may be number, string, or null) into
 * a finite float in years, or null if unparseable.
 */
function _toYears(age) {
  if (age === null || age === undefined || age === "") return null;
  const n = typeof age === "number" ? age : parseFloat(String(age));
  return Number.isFinite(n) ? n : null;
}

/**
 * Format the numeric age the way the app has always done it: months for
 * horses under 2 years, "Xy" otherwise. Kept identical to the legacy
 * `formatAge()` in HorsesPage.jsx so nothing regresses visually.
 */
export function formatBaseAge(age) {
  const y = _toYears(age);
  if (y === null) return "";
  if (y < 2) return `${Math.round(y * 12)} mo`;
  // Preserve fractional ages (e.g. 4.5) but drop trailing zeros.
  const trimmed = Number.isInteger(y) ? String(y) : y.toFixed(2).replace(/\.?0+$/, "");
  return `${trimmed}y`;
}

/**
 * Core helper. Given the horse's stored numeric age, return:
 *   {
 *     baseLabel:    "5y"       — the traditional age display
 *     isRising:     boolean    — true iff the ↑ arrow / Rising label
 *                                should be shown
 *     risingAge:    6          — the age the horse will turn this year
 *     risingLabel:  "Rising 6" — pre-formatted for text substitution
 *   }
 *
 * `now` is injectable for tests; defaults to the current moment.
 */
export function poloAge(age, now = new Date()) {
  const y = _toYears(age);
  if (y === null) {
    return { baseLabel: "", isRising: false, risingAge: null, risingLabel: "" };
  }
  const baseLabel = formatBaseAge(y);

  // Feature dormant until the epoch. Return "just the number" — no
  // rising treatment — for anything before 2027-01-01 UTC.
  if (now < RISING_EPOCH_DATE) {
    return { baseLabel, isRising: false, risingAge: null, risingLabel: "" };
  }

  // The "Rising N+1" convention doesn't really apply to foals (< 2y) —
  // those horses are still counted in months by tradition, and the
  // polo circuit tracks them by foal-year rather than by rising age.
  // Keep them on the plain month display to avoid noise like "Rising 2"
  // on a 4-month-old.
  if (y < 2) {
    return { baseLabel, isRising: false, risingAge: null, risingLabel: "" };
  }

  // Rising age is always floor(current age) + 1. We floor because a
  // horse recorded as 4.5 is "rising 5" (i.e. will turn 5 this year),
  // not rising 6.
  const risingAge = Math.floor(y) + 1;
  return {
    baseLabel,
    isRising: true,
    risingAge,
    risingLabel: `Rising ${risingAge}`,
  };
}

/**
 * Convenience — the label the UI should render in a single string.
 * When rising is active, returns "5y ↑ (Rising 6)" style output for
 * places that don't have room for the badge component (tooltips,
 * export CSVs, log lines).
 */
export function ageDisplayString(age, now = new Date()) {
  const p = poloAge(age, now);
  if (!p.baseLabel) return "";
  if (!p.isRising) return p.baseLabel;
  return `${p.baseLabel} · ${p.risingLabel}`;
}
